/**
 * Compact revenue + bookings chart for the dashboard homepage.
 * Staff+admin only. Shows a 6-month sparkline of revenue paired with a
 * bookings bar chart, plus 3 headline stats.
 */
import { useEffect, useState } from "react";
import { Loader2, TrendingUp } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

import { api } from "@/lib/auth";

const moneyShort = (cents) => {
    const v = (cents || 0) / 100;
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
    return `$${v.toFixed(0)}`;
};
const moneyFull = (cents) =>
    `$${((cents || 0) / 100).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload;
    return (
        <div className="bg-[#0F1E3B] border border-[#FF5A00]/40 px-3 py-2 text-xs font-mono-tech text-white">
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A00] mb-1">{label}</div>
            <div>Revenue · {moneyShort(p.revenue_cents)}</div>
            <div>Bookings · {p.bookings}</div>
            <div>Contracts · {p.contracts}</div>
        </div>
    );
};

export default function RevenueSnapshot() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [period, setPeriod] = useState("month");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/reports/summary?period=${period}`);
                setData(data);
            } catch (e) {
                if (e?.response?.status === 403) return; // silently no-op for non-staff
                setErr(e?.response?.data?.detail || "Could not load reports.");
            } finally { setLoading(false); }
        })();
    }, [period]);

    if (loading) {
        return (
            <div className="mt-16 border border-white/10 bg-[#1B2E55]/50 p-6 flex items-center gap-2 text-xs text-slate-400"
                data-testid="dashboard-reports-loading">
                <Loader2 size={12} className="animate-spin" /> Loading revenue snapshot…
            </div>
        );
    }
    if (err || !data) return null;

    const chartRows = (data.months || []).map((m) => ({
        ...m,
        revenue_aud: (m.revenue_cents || 0) / 100,
    }));

    return (
        <div className="mt-16 border border-white/10 bg-[#1B2E55]/50 p-6" data-testid="dashboard-reports-card">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <TrendingUp size={14} className="text-[#FF5A00]" />
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ Revenue snapshot</div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex border border-white/15" data-testid="dashboard-reports-period-toggle">
                        {[["month", "Month"], ["quarter", "Quarter"], ["year", "Year"]].map(([key, label]) => (
                            <button
                                key={key}
                                onClick={() => setPeriod(key)}
                                data-testid={`dashboard-reports-period-${key}`}
                                className={`px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest transition-colors ${period === key ? "bg-[#FF5A00] text-white" : "text-slate-400 hover:text-white"}`}
                            >{label}</button>
                        ))}
                    </div>
                    <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500" data-testid="dashboard-reports-range-label">
                        Last {chartRows.length} {period === "month" ? "months" : period === "quarter" ? "quarters" : "years"}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5">
                <Stat k="Revenue" v={moneyFull(data.total_revenue_cents)} testid="dashboard-reports-revenue" />
                <Stat k="Bookings" v={data.total_bookings} testid="dashboard-reports-bookings" />
                <Stat k="Contracts" v={data.total_contracts_signed} testid="dashboard-reports-contracts" />
            </div>

            <div className="h-52 -mx-2" data-testid="dashboard-reports-chart">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartRows} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                        <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                        <XAxis dataKey="label" stroke="#94A3B8" tickLine={false} axisLine={false}
                            tick={{ fontSize: 10, fontFamily: "ui-monospace, SFMono-Regular, monospace", fill: "#94A3B8" }} />
                        <YAxis yAxisId="left" hide />
                        <YAxis yAxisId="right" orientation="right" hide />
                        <Tooltip
                            cursor={{ fill: "rgba(255,255,255,0.03)" }}
                            content={<ChartTooltip />}
                        />
                        <Bar yAxisId="left" dataKey="bookings" fill="rgba(255,255,255,0.16)" radius={[2, 2, 0, 0]} />
                        <Line yAxisId="right" type="monotone" dataKey="revenue_aud" stroke="#FF5A00" strokeWidth={2}
                            dot={{ r: 3, fill: "#FF5A00", strokeWidth: 0 }} activeDot={{ r: 5 }} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

const Stat = ({ k, v, testid }) => (
    <div className="border border-white/10 bg-[#0F1E3B]/40 p-4" data-testid={testid}>
        <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500">/ {k}</div>
        <div className="mt-1 font-display font-black tracking-tighter text-2xl text-white">{v}</div>
    </div>
);
